import { Ban, SearchX } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { RESPONSE_STATUSES } from "@/api/constants";
import { getAppHomePath } from "@/config/path";
import { getApiErrorStatus } from "@/utils/getApiErrorStatus";

import { CustomButton } from "../Button";

interface ResourceErrorViewProps {
  error?: unknown;
}

const getListPath = (pathname: string): string => {
  const path = pathname.replace(/\/$/, "");
  const lastSlash = path.lastIndexOf("/");

  if (lastSlash <= 0) {
    return "/";
  }

  return path.slice(0, lastSlash);
};

export const ResourceErrorView = ({ error }: ResourceErrorViewProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const status = getApiErrorStatus(error);
  const isForbidden = status === RESPONSE_STATUSES.FORBIDDEN;
  const dashboardPath = getAppHomePath(pathname);
  const listPath = getListPath(pathname);

  return (
    <div
      className="flex flex-col items-center justify-center p-8 text-gray-500 h-full w-full min-h-[400px]"
      data-testid={
        isForbidden ? "forbidden-error-view" : "not-found-error-view"
      }
    >
      {isForbidden ? (
        <Ban className="w-12 h-12 mb-4 text-gray-300" />
      ) : (
        <SearchX className="w-12 h-12 mb-4 text-gray-300" />
      )}
      <h2 className="text-xl font-bold text-gray-700 mb-2">
        {isForbidden ? "Access Denied" : "Record not found"}
      </h2>
      <p className="text-sm text-center mb-6 max-w-md">
        {isForbidden
          ? "You don't have permission to view this page."
          : "The record you are looking for doesn't exist or is no longer available."}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        {isForbidden ? (
          <>
            <CustomButton
              type="button"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              Go back
            </CustomButton>
            <CustomButton asChild>
              <Link to={dashboardPath}>Go to Dashboard</Link>
            </CustomButton>
          </>
        ) : (
          <>
            <CustomButton asChild>
              <Link to={listPath}>Back to list</Link>
            </CustomButton>
            <CustomButton asChild variant="secondary">
              <Link to={dashboardPath}>Go to Dashboard</Link>
            </CustomButton>
          </>
        )}
      </div>
    </div>
  );
};
